import React, { FunctionComponent, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { isAuthorized } from 'auth/Auth';
import { Overlay } from 'core/overlay/Overlay';
import { useReview, ReviewActionType } from 'reviews2/feed/review-yeti/Review.store';
import { ContextProps, ReadOnlyReason } from 'reviews2/feed/review-yeti/Review.types';
import { AttributionType } from 'reviews2/feed/Reviews.types';
import { removeAttribution } from 'reviews2/Reviews.api';

import 'reviews2/feed/review-yeti/attribution/ReviewAttribution.scss';

interface Props {
    context: ContextProps['context'];
    attribution: any;
}

const AttributionTypeToLabel = {
    [AttributionType.Manual]: 'REVIEW_ATTRIBUTION_MANUAL',
    [AttributionType.Mention]: 'REVIEW_ATTRIBUTION_MENTION',
    [AttributionType.Requestor]: 'REVIEW_ATTRIBUTION_REQUESTOR',
};

const AttributionTypeToIcon = {
    [AttributionType.Manual]: 'icon-plus-circle',
    [AttributionType.Mention]: 'icon-comment-alt',
    [AttributionType.Requestor]: 'icon-paper-plane',
};

const OVERLAY_AREA = 'r4e-review-attribution';

export const ReviewAttribution: FunctionComponent<Props> = ({ attribution, context }) => {
    const { t } = useTranslation('reviews2');
    const [{ review }, dispatch] = useReview();
    const { options } = context;
    const { type, user } = attribution;

    const remove = useCallback(
        async (userId: number) => {
            const args = { userId, attributeType: type };

            const { user_attribution = [] } = await trackPromise(
                removeAttribution(review!.r4e_tenant_id, review!.document_id, args),
                `${OVERLAY_AREA}-${review!.document_id}`,
            );

            dispatch({
                type: ReviewActionType.UpdateReview,
                review: {
                    user_attribution,
                },
            });
        },
        [review, type, dispatch],
    );

    if (!review || !user || attribution.visible === false) {
        return null;
    }

    return (
        <div className="r4e-review-attribution">
            <Overlay area={`${OVERLAY_AREA}-${review.document_id}`} />
            <div className="type-icon">
                <i className={AttributionTypeToIcon[type as AttributionType]} />
            </div>
            <div className="type-label">
                <strong>{t(AttributionTypeToLabel[type as AttributionType], { name: user?.displayName })}</strong>
            </div>
            {isAuthorized({ actions: [ActionId.ReviewAttributions.Delete] }) &&
                ![ReadOnlyReason.View, ReadOnlyReason.Reports].includes(options?.readOnlyReason as ReadOnlyReason) && (
                    <div tabIndex={0} role="button" onClick={() => remove(user.id)} onKeyDown={() => remove(user.id)}>
                        <i className="icon-times" />
                    </div>
                )}
        </div>
    );
};
